import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { http_put } from '../utils/api_client';
import { API_ENDPOINTS, ENDPOINTS } from '../utils/endpoints';
import AddItemPicker from './AddItemPicker';
import ChecklistDistribution from './ChecklistDistribution';

const DND_TYPE = 'checklist-item';

// Map a referenced material's type to an icon class + short label.
const typeMeta = (materialType) => {
  switch ((materialType || '').toLowerCase()) {
    case 'walkthrough': return { cls: 'walk', label: 'Walkthrough' };
    case 'docs':
    case 'knowledge': return { cls: 'kb', label: 'Knowledge base' };
    case 'paths':
    default: return { cls: 'path', label: 'Path' };
  }
};

const ItemRow = ({ item, index, moveItem, onToggleRequired, onRemove }) => {
  const rowRef = useRef(null);
  const gripRef = useRef(null);
  const meta = typeMeta(item.material_type);

  const [{ isDragging }, drag, preview] = useDrag({
    type: DND_TYPE,
    item: { index },
    collect: (m) => ({ isDragging: m.isDragging() })
  });

  const [, drop] = useDrop({
    accept: DND_TYPE,
    hover(dragged) {
      if (!rowRef.current) return;
      if (dragged.index === index) return;
      moveItem(dragged.index, index);
      dragged.index = index;
    }
  });

  preview(drop(rowRef));
  drag(gripRef);

  return (
    <div ref={rowRef} className={`cl-item ${isDragging ? 'is-dragging' : ''}`}>
      <span ref={gripRef} className="cl-item__grip" title="Drag to reorder">⣿</span>
      <span className={`cl-ic ${meta.cls}`} aria-hidden>{meta.label[0]}</span>
      <div className="cl-item__body">
        <div className="cl-item__t">{item.name || 'Untitled'}</div>
        <div className="cl-item__m">{meta.label}</div>
      </div>
      <span
        className={`cl-item__req ${item.required ? 'on' : 'off'}`}
        onClick={() => onToggleRequired(index)}
        title="Toggle required / optional"
      >
        {item.required ? 'Required' : 'Optional'}
      </span>
      <span className="cl-item__rm" onClick={() => onRemove(index)} title="Remove">✕</span>
    </div>
  );
};

// BL-084 — the in-app checklist builder. Left pane: editable title/description +
// ordered, drag-reorderable items with per-item required toggle. Right pane:
// Distribution (built in Wave 3). Edits autosave to PUT /checklists/:slug.
const ChecklistBuilder = ({ slug, companySlug, initial }) => {
  const [name, setName] = useState(initial.name || '');
  const [headerKicker, setHeaderKicker] = useState(initial.header_kicker || '');
  const [description, setDescription] = useState(initial.description || '');
  const [items, setItems] = useState(() => (initial.items || []).map((i) => ({
    learning_material_id: i.learning_material_id,
    required: i.required !== false,
    name: i.name,
    slug: i.slug,
    material_type: i.material_type
  })));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saveState, setSaveState] = useState('saved'); // saved | saving
  const [publishing, setPublishing] = useState(false);
  const firstRun = useRef(true);

  const excludeIds = new Set([initial.id, ...items.map((i) => i.learning_material_id)]);

  const moveItem = useCallback((from, to) => {
    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }, []);

  const toggleRequired = (index) => setItems((prev) => prev.map((it, i) => (i === index ? { ...it, required: !it.required } : it)));
  const removeItem = (index) => setItems((prev) => prev.filter((_, i) => i !== index));
  const addItem = (material) => {
    setItems((prev) => (
      prev.some((i) => i.learning_material_id === material.id)
        ? prev
        : [...prev, { learning_material_id: material.id, required: true, name: material.name, slug: material.slug, material_type: material.material_type }]
    ));
  };

  // Debounced autosave on any content change (skip the initial mount).
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return undefined; }
    setSaveState('saving');
    const t = setTimeout(() => {
      http_put(API_ENDPOINTS.PUT_CHECKLIST(slug), {
        name,
        description,
        header_kicker: headerKicker,
        items: items.map((i) => ({ learning_material_id: i.learning_material_id, required: i.required }))
      })
        .then(() => setSaveState('saved'))
        .catch(() => { setSaveState('saved'); toast.error('Could not save changes'); });
    }, 900);
    return () => clearTimeout(t);
  }, [name, description, headerKicker, items, slug]);

  // Preview opens an admin preview of the learner page from the current draft
  // (no publish required). Real learners see the published snapshot via the hub.
  const preview = () => { window.location.href = `/${companySlug}/checklists/${slug}`; };

  const publish = () => {
    setPublishing(true);
    http_put(API_ENDPOINTS.PUBLISH_CHECKLIST(slug), {})
      .then(() => toast.success('Checklist published'))
      .catch(() => toast.error('Could not publish'))
      .finally(() => setPublishing(false));
  };

  return (
    <div className="cl-builder">
      <ToastContainer position="bottom-right" autoClose={3000} />

      <div className="cl-tbar">
        <div className="cl-tbar__bc">Checklists / <b>{name || 'Untitled checklist'}</b></div>
        <div className="cl-tbar__sp" />
        <span className={`cl-tbar__save ${saveState}`}>{saveState === 'saving' ? 'Saving…' : 'Saved'}</span>
        <button type="button" className="cl-btn cl-btn--white" onClick={preview}>Preview</button>
        <button type="button" className="cl-btn cl-btn--primary" onClick={publish} disabled={publishing}>
          {publishing ? 'Publishing…' : 'Publish'}
        </button>
      </div>

      <div className="cl-grid">
        <div className="cl-card">
          <input
            className="cl-kickerfield"
            value={headerKicker}
            placeholder="Your onboarding plan"
            title="Header label — the small eyebrow shown above the title on the learner page"
            onChange={(e) => setHeaderKicker(e.target.value)}
          />
          <input
            className="cl-titlefield"
            value={name}
            placeholder="Untitled checklist"
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className="cl-descfield"
            value={description}
            placeholder="Add a short description…"
            onChange={(e) => setDescription(e.target.value)}
          />

          <div className="cl-itemshead">Items · {items.length}</div>

          {items.length === 0 && (
            <div className="cl-empty">No items yet. Add paths, walkthroughs, or knowledge bases below.</div>
          )}

          {items.map((item, index) => (
            <ItemRow
              key={item.learning_material_id}
              item={item}
              index={index}
              moveItem={moveItem}
              onToggleRequired={toggleRequired}
              onRemove={removeItem}
            />
          ))}

          <div className="cl-additem" onClick={() => setPickerOpen(true)}>＋ Add item</div>
        </div>

        <ChecklistDistribution
          checklistId={initial.id}
          slug={slug}
          companySlug={companySlug}
          initialVisibility={initial.visibility}
        />
      </div>

      {pickerOpen && (
        <AddItemPicker
          excludeIds={excludeIds}
          onAdd={addItem}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
};

export default ChecklistBuilder;
